// src/components/character/creation/steps/Background.js
import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Feather, RefreshCw, Book, Scroll, Heart, 
  Compass, Shield, Star, AlertCircle 
} from 'lucide-react';
import { generateBackstory } from '../../../../services/storyGenerator';
import { BACKGROUNDS } from '../../../../game-system/character/backgrounds';

const PERSONALITY_CATEGORIES = {
  personalityTraits: {
    label: "Personality Traits",
    icon: Heart,
    description: "How does your character behave and interact?",
    placeholder: "I always see the best in people..."
  },
  ideals: {
    label: "Ideals",
    icon: Star,
    description: "What principles guide your character?",
    placeholder: "Honor above all else..."
  },
  bonds: {
    label: "Bonds",
    icon: Shield,
    description: "What ties your character to the world?",
    placeholder: "I have sworn to protect my homeland..."
  },
  flaws: {
    label: "Flaws",
    icon: AlertCircle,
    description: "What weaknesses shape your character?",
    placeholder: "I can't resist a pretty face..."
  }
};

export const Background = ({ character, onUpdate, onError, onSuccess }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const [selectedBackground, setSelectedBackground] = useState(character.background || '');

  const handleBackgroundChange = useCallback((backgroundId) => {
    const background = BACKGROUNDS[backgroundId];
    setSelectedBackground(backgroundId);
    onUpdate({
      background: backgroundId,
      proficiencies: {
        ...character.proficiencies,
        skills: [...character.proficiencies.skills, ...background.skillProficiencies]
      },
      equipment: {
        ...character.equipment,
        items: [...character.equipment.items, ...background.startingEquipment]
      }
    });
  }, [character, onUpdate]);

  const generateStory = useCallback(async () => {
    if (!character.race || !character.alignment) {
      onError('Please complete your character details first');
      return;
    }

    setIsGenerating(true);
    try {
      const story = await generateBackstory({
        name: character.name,
        race: character.race,
        background: selectedBackground,
        alignment: character.alignment,
        attributes: character.attributes,
        skills: character.skills,
        personality: {
          traits: character.personalityTraits,
          ideals: character.ideals,
          bonds: character.bonds,
          flaws: character.flaws
        }
      });

      onUpdate({
        backstory: story.narrative,
        personalityTraits: story.personalityTraits || character.personalityTraits,
        ideals: story.ideals || character.ideals,
        bonds: story.bonds || character.bonds,
        flaws: story.flaws || character.flaws
      });
      onSuccess('Your tale has been woven!');
    } catch (error) {
      console.error('Backstory generation error:', error);
      onError('The story weaving failed. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }, [character, selectedBackground, onUpdate, onError, onSuccess]);

  const BackgroundSelection = () => (
    <motion.div
      className="character-creation__background-selection"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <h3 className="character-creation__section-title">
        <Book size={20} />
        Choose Your Background
      </h3>
      <div className="character-creation__background-grid">
        {Object.entries(BACKGROUNDS).map(([id, background]) => (
          <motion.div
            key={id}
            className={`character-creation__background-card ${
              selectedBackground === id ? 'character-creation__background-card--selected' : ''
            }`}
            onClick={() => handleBackgroundChange(id)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <h4>{background.name}</h4>
            <p>{background.description}</p>
            <div className="character-creation__background-features">
              <div>
                <strong>Skills:</strong>
                <span>{background.skillProficiencies.join(', ')}</span>
              </div>
              <div>
                <strong>Tools:</strong>
                <span>{background.toolProficiencies.join(', ') || 'None'}</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );

  const PersonalityEditor = ({ category, value, onChange }) => {
    const Icon = PERSONALITY_CATEGORIES[category].icon;
    
    return (
      <motion.div
        className="character-creation__personality-section"
        initial={false}
        animate={{ height: expandedSection === category ? 'auto' : '48px' }}
      >
        <div
          className="character-creation__personality-header"
          onClick={() => setExpandedSection(expandedSection === category ? null : category)}
        >
          <Icon size={20} />
          <h4>{PERSONALITY_CATEGORIES[category].label}</h4>
        </div>
        
        <AnimatePresence>
          {expandedSection === category && (
            <motion.div
              className="character-creation__personality-content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <p className="character-creation__personality-description">
                {PERSONALITY_CATEGORIES[category].description}
              </p>
              <textarea
                className="character-creation__textarea"
                value={value}
                onChange={(e) => onChange({ [category]: e.target.value })}
                placeholder={PERSONALITY_CATEGORIES[category].placeholder}
                rows={3}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  return (
    <motion.div
      className="character-creation__content"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
    >
      {/* Background Selection */}
      <BackgroundSelection />

      {/* Personality Traits */}
      <div className="character-creation__section">
        <h3 className="character-creation__section-title">
          <Compass size={20} />
          Define Your Character
        </h3>
        <div className="character-creation__personality-grid">
          {Object.keys(PERSONALITY_CATEGORIES).map(category => (
            <PersonalityEditor
              key={category}
              category={category}
              value={character[category] || ''}
              onChange={onUpdate}
            />
          ))}
        </div>
      </div>

      {/* Backstory */}
      <div className="character-creation__section">
        <h3 className="character-creation__section-title">
          <Scroll size={20} />
          Your Legend
        </h3>
        <div className="character-creation__backstory">
          <div className="character-creation__input-group">
            <textarea
              className="character-creation__textarea"
              value={character.backstory}
              onChange={(e) => onUpdate({ backstory: e.target.value })}
              placeholder="Write your character's tale..."
              rows={8}
            />
            <button
              className="character-creation__button character-creation__button--secondary"
              onClick={generateStory}
              disabled={isGenerating}
            >
              {isGenerating ? (
                <>
                  <RefreshCw className="spin-animation" />
                  Weaving Tale...
                </>
              ) : (
                <>
                  <Feather />
                  Generate Story
                </>
              )}
            </button>
          </div>

          {selectedBackground && (
            <div className="character-creation__background-prompts">
              <h4>Story Prompts</h4>
              <ul>
                {BACKGROUNDS[selectedBackground].storyPrompts.map((prompt, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    {prompt}
                  </motion.li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Preview Section */}
      {character.backstory && (
        <motion.div
          className="character-creation__story-preview"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h3>Story Preview</h3>
          <div className="character-creation__story-content">
            {character.backstory.split('\n').map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default Background;
